import {
	App,
	AppError,
	AppMenuPoint,
	AppMenuPoints,
	AppNavMenuPointsService,
	ErrorService,
	ErrorType,
	getEnvironmentFromUrl,
	MenuPointService,
	NavVariant,
	Role,
	SelftestRoutes,
} from '@agilox/common';
import {
	CookieOverlayComponent,
	ErrorContentComponent,
	ErrorPageComponent,
	NavModule,
	ToastContainerComponent,
} from '@agilox/components';
import { OverlayComponent } from '@agilox/ui';
import { MenuPoint, MenuPointCallbackAction } from '@agilox/ui-common';
import { LowerCasePipe } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	Signal,
	ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserStoreService } from '@user-store';
import { filter } from 'rxjs';

@Component({
	selector: 'selftest-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		NavModule,
		RouterOutlet,
		OverlayComponent,
		ErrorContentComponent,
		ErrorPageComponent,
		CookieOverlayComponent,
		ToastContainerComponent,
		TranslatePipe,
		LowerCasePipe,
	],
})
export class AppComponent {
	private translate = inject(TranslateService);
	private menuPointService = inject(MenuPointService);
	private appNavMenuPointsService = inject(AppNavMenuPointsService);
	private router = inject(Router);
	private errorService = inject(ErrorService);
	private userStoreService = inject(UserStoreService);
	private authService = inject(AuthService);

	serverError = computed(() => this.errorService.serverError());

	private role = computed(() => this.userStoreService.currentUser()?.role);
	private language = computed(() => this.userStoreService.currentUser()?.lang || 'en');

	private roleAndLanguage = computed(() => {
		return {
			role: this.role(),
			language: this.language(),
		};
	});

	public menuPoints = computed(() =>
		this.menuPointService.getMenuPointsByCategory(
			NavVariant.DESKTOP,
			this.roleAndLanguage().role as Role
		)
	);

	public mobileMenuPoints = computed(() =>
		this.menuPointService.getMenuPointsByCategory(
			NavVariant.MOBILE,
			this.roleAndLanguage().role as Role
		)
	);

	public appNavMenuPoints = computed(() =>
		this.appNavMenuPointsService.getAppNavMenuPoints(this.roleAndLanguage().role as Role)
	);

	public currentUrl = computed(() => {
		return this.navigationEnd()?.urlAfterRedirects || this.router.url;
	});

	environment: string = getEnvironmentFromUrl(SelftestRoutes);

	activeApp: AppMenuPoint | undefined = AppMenuPoints.find((app) => app.name === 'selftest');

	private navigationEnd = toSignal(
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
	);

	private navigationError = toSignal(
		this.router.events.pipe(filter((event) => event instanceof NavigationError))
	);

	error: Signal<AppError | null> = toSignal(this.errorService.error$);
	constructor() {
		effect(() => {
			const language = this.userStoreService.currentUser()?.lang;
			this.translate.use(language || 'en');
		});

		effect(() => {
			const error = this.navigationError();
			if (error) {
				this.handleErrors(error);
			}
		});
	}

	private handleErrors(error: NavigationError): void {
		if (!navigator.onLine) {
			this.errorService.setError({
				application: App.my,
				type: ErrorType.NO_INTERNET_ERROR,
			});
		} else if (error.error.toString().includes('chunk')) {
			this.errorService.setError({
				application: App.my,
				type: ErrorType.CHUNK_ERROR,
			});
		}
	}

	onCloseError(): void {
		this.errorService.clearError();
	}

	logout(): void {
		this.authService.logout();
	}

	navCallback(menuPoint: MenuPoint): void {
		if (menuPoint.callbackAction === MenuPointCallbackAction.LOGOUT) {
			this.authService.logout();
		}
	}
}
